// Bulandshahr — shared visual primitives for the Southwestern-inspired revamp.
// Ink + gold + off-white paper palette; Playfair Display serif for display; Source Sans 3 for body.
// All page-level headers, section headings, buttons and pull quotes live here so the whole site
// can be re-themed from a single file.

import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X, ArrowRight } from "lucide-react";

// ---- Design tokens ---------------------------------------------------------
export const PALETTE = {
  ink: "#0F0F10",         // deep black — nav / footer / headlines
  inkSoft: "#2A2A2B",     // slightly lighter black for body text
  gold: "#FDBB30",        // signature Southwestern-esque gold
  goldDeep: "#E19E14",    // hover / active gold
  paper: "#F8F5EE",       // warm off-white background
  paperDeep: "#EDE7DA",   // deeper paper — feature bands, cards
  rule: "#D9D2C1",        // divider
  muted: "#726C61",       // secondary text
};

export const FONT = {
  serif: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
  sans:  "'Source Sans 3', 'Inter', system-ui, sans-serif",
};

// ---- Buttons & primitives --------------------------------------------------
export function GoldButton({ children, className = "", testid, as = "button", ...props }) {
  const Comp = as;
  return (
    <Comp
      data-testid={testid}
      className={`inline-flex items-center gap-2 bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] font-semibold uppercase tracking-[0.2em] text-[11px] px-6 py-3 rounded-none transition-colors ${className}`}
      {...props}
    >
      {children}
      <ArrowRight size={13} strokeWidth={2.4} />
    </Comp>
  );
}

export function GhostButton({ children, className = "", testid, as = "button", ...props }) {
  const Comp = as;
  return (
    <Comp
      data-testid={testid}
      className={`inline-flex items-center gap-2 border-b-2 border-[#0F0F10] hover:border-[#FDBB30] text-[#0F0F10] hover:text-[#0F0F10] font-semibold uppercase tracking-[0.2em] text-[11px] pb-1 transition-colors ${className}`}
      {...props}
    >
      {children}
      <ArrowRight size={13} strokeWidth={2.4} />
    </Comp>
  );
}

// Eyebrow line (small caps + gold rule on top). Used before every section heading.
export function Eyebrow({ children, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#726C61] font-semibold ${className}`}>
      <span className="inline-block w-8 h-[2px] bg-[#FDBB30]" />
      <span>{children}</span>
    </div>
  );
}

// Section heading — big serif on ink text.
export function SectionHeading({ children, className = "", as: As = "h2" }) {
  return (
    <As
      className={`font-serif text-[#0F0F10] tracking-tight leading-[1.1] text-[34px] md:text-[46px] ${className}`}
      style={{ fontFamily: FONT.serif }}
    >
      {children}
    </As>
  );
}

// ---- Page-level masthead ---------------------------------------------------
// Big paper masthead — replaces the old amber gradient hero. Every non-Home page
// starts with this so headers feel consistent across the site.
export function PageHero({ eyebrow, title, kicker }) {
  return (
    <section
      className="relative bg-[#F8F5EE] border-b border-[#D9D2C1] py-16 md:py-24"
      data-testid="bul-page-hero"
    >
      <div className="relative max-w-7xl mx-auto px-6">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1
          className="mt-4 font-serif text-4xl md:text-6xl tracking-tight text-[#0F0F10] leading-[1.05] max-w-4xl"
          style={{ fontFamily: FONT.serif }}
        >
          {title}
        </h1>
        {kicker && (
          <p className="mt-5 max-w-2xl text-[#2A2A2B] text-[15px] md:text-base leading-relaxed">
            {kicker}
          </p>
        )}
        <div className="mt-8 h-[3px] w-20 bg-[#FDBB30]" />
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-6 py-14 md:py-20 ${className}`}>
      {children}
    </section>
  );
}

// Pull quote — used for testimonials / mission statements.
export function PullQuote({ quote, attribution, className = "" }) {
  return (
    <blockquote
      className={`border-l-4 border-[#FDBB30] pl-6 md:pl-8 py-2 ${className}`}
      data-testid="bul-pull-quote"
    >
      <p
        className="font-serif text-2xl md:text-3xl text-[#0F0F10] leading-snug italic"
        style={{ fontFamily: FONT.serif }}
      >
        &ldquo;{quote}&rdquo;
      </p>
      {attribution && (
        <footer className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#726C61] font-semibold">
          — {attribution}
        </footer>
      )}
    </blockquote>
  );
}

// Editorial 2-column feature (image + narrative). Optional `flip` swaps sides.
export function FeatureRow({ eyebrow, title, body, cta, image, flip = false, testid }) {
  return (
    <section className="py-14 md:py-20 border-b border-[#D9D2C1] last:border-b-0" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div className={`${flip ? "md:order-2" : "md:order-1"}`}>
          {image ? (
            <img src={image} alt={title} className="w-full h-[320px] md:h-[440px] object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-[320px] md:h-[440px] bg-[#EDE7DA] flex items-center justify-center text-[#726C61] text-xs uppercase tracking-widest">
              Photo coming soon
            </div>
          )}
        </div>
        <div className={`${flip ? "md:order-1" : "md:order-2"}`}>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <SectionHeading className="mt-4">{title}</SectionHeading>
          {body && <p className="mt-5 text-[#2A2A2B] leading-[1.8] text-[15px]">{body}</p>}
          {cta && (
            <div className="mt-7">
              <GhostButton as={cta.as || "a"} href={cta.href} to={cta.to} testid={cta.testid}>
                {cta.label}
              </GhostButton>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Stat card — big number + label, used for the recognition strip.
export function StatCard({ number, label, source, sourceHref, testid }) {
  return (
    <div className="bg-[#0F0F10] text-white p-8 md:p-10 flex-1" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#FDBB30] font-semibold">
        {label}
      </div>
      <div
        className="mt-3 font-serif text-3xl md:text-5xl leading-tight"
        style={{ fontFamily: FONT.serif }}
      >
        {number}
      </div>
      {source && (
        sourceHref ? (
          <a
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block text-[11px] uppercase tracking-[0.25em] text-[#FDBB30] hover:underline"
          >
            {source}
          </a>
        ) : (
          <div className="mt-5 text-[11px] uppercase tracking-[0.25em] text-[#726C61]">{source}</div>
        )
      )}
    </div>
  );
}

// ---- Lightbox (photo carousel modal) ---------------------------------------
export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bul-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={22} />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIdx((idx - 1 + photos.length) % photos.length); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onIdx((idx + 1) % photos.length); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={cur.image_url || cur.url}
          alt={cur.caption || ""}
          className="max-w-full max-h-[80vh] object-contain"
        />
        {cur.caption && (
          <figcaption className="mt-3 text-center text-white/90 text-sm">
            {cur.caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

// ---- Small helpers ---------------------------------------------------------
export function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export function slugify(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml
    ? content
    : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

export const STATIC_CONTENT = {
  about_gsm: `Gramaudyogik Shikshan Mandal, popularly known as GSM, is the parent educational trust established in 1975 under the Bombay Public Trust Act and also registered under the Societies Registration Act. Over the decades, GSM has emerged as a reputed educational organization committed to providing quality technical and professional education in India. The organization has grown organically across three major regions of India — Maharashtra (Western India), Delhi (National Capital Region), and Uttar Pradesh (Northern India). Presently, GSM manages a network of educational institutions and learning centres at multiple locations across the country.`,
  about_mit: `MIT Bulandshahr is a flagship institution of Gramaudyogik Shikshan Mandal, established to conduct AICTE-approved technical education programmes affiliated to AKTU Lucknow. The institution is dedicated to developing innovative technologies and nurturing technically competent, creative, and socially responsible professionals capable of addressing complex global challenges. With standardized academic and administrative processes, the college is committed to quality education and continuous improvement, and takes pride in its expanding alumni network of MITians contributing successfully across industries, entrepreneurship, research, and educational institutions worldwide.`,
  vision: `To become a globally recognized technical institution in skill and competency development for acquiring latest requirements of industry by keeping social and environmental awareness.`,
  mission: `Institute aims at imparting student-centric quality education through teaching-learning process and training for engineering students so that they are able to start their own business, employable at the best positions in the field of engineering & technology, and achieve higher education capability to become the best citizens.`,
  contact: {
    title: "Marathwada Institute of Technology, Bulandshahr",
    address: "MIT Campus, Bulandshahr, Uttar Pradesh, INDIA",
    phones: [],
    email: "info@mit.asia",
    website: "http://bulandshahr.mit.asia",
    head_office: {
      title: "MIT, Head Office",
      address: "MIT, Beed Bypass Road, Satara Village Road, Chhatrapati Sambhajinagar – 431010, Maharashtra State, INDIA",
      phones: ["+91 240 2375375"],
      fax: "+91 240 2375275, 2376154",
      email: "info@mit.asia",
      website: "www.mit.asia",
    },
  },
};
